import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

interface TooltipProps {
    children: React.ReactNode;
    content: React.ReactNode;
    align?: 'left' | 'center' | 'right';
}

const Tooltip: React.FC<TooltipProps> = ({ children, content, align = 'center' }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isFixed, setIsFixed] = useState(false);
    const [tooltipState, setTooltipState] = useState({
        top: 0,
        left: 0,
        placement: 'top' as 'top' | 'bottom',
        maxHeight: 500
    });
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const isVisible = isHovered || isFixed;

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (isFixed &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target as Node) &&
                tooltipRef.current &&
                !tooltipRef.current.contains(event.target as Node)
            ) {
                setIsFixed(false);
                setIsHovered(false);
            }
        };

        if (isFixed) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isFixed]);

    useEffect(() => {
        if (isVisible && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;

            const spaceAbove = rect.top;
            const spaceBelow = viewportHeight - rect.bottom;

            // Prefer top unless space is tight (< 300px) and bottom has more space
            let placement: 'top' | 'bottom' = 'top';
            if (spaceAbove < 300 && spaceBelow > spaceAbove) {
                placement = 'bottom';
            }

            let top = 0;
            let maxHeight = 0;

            if (placement === 'top') {
                top = rect.top - 10;
                maxHeight = rect.top - 20; // 20px padding from top
            } else {
                top = rect.bottom + 10;
                maxHeight = spaceBelow - 20; // 20px padding from bottom
            }

            // Position tooltip positions
            let left = 0;
            switch (align) {
                case 'left':
                    left = rect.left;
                    break;
                case 'right':
                    left = rect.right;
                    break;
                case 'center':
                default:
                    left = rect.left + rect.width / 2;
                    break;
            }

            setTooltipState({
                top,
                left,
                placement,
                maxHeight
            });
        }
    }, [isVisible, align]);

    const getTransformClass = () => {
        let trans = '';
        switch (align) {
            case 'left': trans = ''; break;
            case 'right': trans = '-translate-x-full'; break;
            case 'center':
            default: trans = '-translate-x-1/2'; break;
        }

        if (tooltipState.placement === 'top') {
            trans += ' -translate-y-full';
        }

        return trans;
    };

    return (
        <>
            <div
                ref={triggerRef}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={(e) => {
                    e.stopPropagation();
                    setIsFixed(!isFixed);
                }}
                className="inline-flex cursor-pointer"
            >
                {children}
            </div>
            {isVisible && ReactDOM.createPortal(
                <div
                    ref={tooltipRef}
                    className={`fixed z-[99999] transform ${getTransformClass()} overflow-y-auto no-scrollbar shadow-2xl rounded-lg border border-slate-200`}
                    style={{
                        top: tooltipState.top,
                        left: tooltipState.left,
                        maxHeight: tooltipState.maxHeight
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    onClick={(e) => e.stopPropagation()}
                >
                    {content}
                    {isFixed && (
                        <div className="absolute top-2 right-2 z-50">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsFixed(false);
                                    setIsHovered(false);
                                }}
                                className="p-1.5 bg-slate-100/90 hover:bg-red-50 rounded-full text-slate-400 hover:text-red-500 transition-colors shadow-sm"
                                title="Close Sticky Tooltip"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                    )}
                </div>,
                document.body
            )}
        </>
    );
};

export default Tooltip;
